import { useEffect, useRef, useCallback } from 'react';

interface FormAbandonmentConfig {
  formName: string;
  pagePath: string;
  totalFields: number;
}

interface FormData {
  [key: string]: any;
}

export function useFormAbandonment(
  formData: FormData,
  isSubmitted: boolean,
  config: FormAbandonmentConfig
) {
  const startTimeRef = useRef<number>(Date.now());
  const lastFieldRef = useRef<string>('');
  const hasLoggedRef = useRef<boolean>(false);

  // Get session ID from existing analytics cookie if available
  const getSessionId = useCallback((): string | null => {
    try {
      // Try to get from existing traffic analytics
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'traffic_session_id') {
          return value;
        }
      }
    } catch (e) {
      // Ignore cookie errors
    }
    return null;
  }, []);

  // Calculate filled fields count
  const getFilledFieldsCount = useCallback((data: FormData): number => {
    return Object.entries(data).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value; // unchecked boxes are not "filled"
      return true;
    }).length;
  }, []);

  // Get device type
  const getDeviceType = useCallback((): string => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }, []);

  // Auto-detect the last field the visitor changed
  const prevDataRef = useRef<FormData>(formData);
  useEffect(() => {
    for (const key of Object.keys(formData)) {
      if (formData[key] !== prevDataRef.current[key]) lastFieldRef.current = key;
    }
    prevDataRef.current = formData;
  }, [formData]);

  // Track last field touched
  const trackFieldChange = useCallback((fieldName: string) => {
    lastFieldRef.current = fieldName;
  }, []);

  // Log abandonment to backend
  const logAbandonment = useCallback(() => {
    // Don't log if already logged, form was submitted, or no fields filled
    if (hasLoggedRef.current || isSubmitted) return;
    
    const filledCount = getFilledFieldsCount(formData);
    if (filledCount === 0) return;

    hasLoggedRef.current = true;

    const timeOnForm = Math.round((Date.now() - startTimeRef.current) / 1000);
    
    const payload = {
      session_id: getSessionId(),
      page_path: config.pagePath,
      form_name: config.formName,
      fields_filled: formData,
      fields_count: filledCount,
      total_fields: config.totalFields,
      time_on_form_seconds: timeOnForm,
      last_field_touched: lastFieldRef.current || null,
      device_type: getDeviceType()
    };

    // Use sendBeacon for reliable exit tracking
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-form-abandonment`;
    
    try {
      navigator.sendBeacon(url, JSON.stringify(payload));
    } catch (e) {
      // Fallback to fetch if sendBeacon fails
      fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => {});
    }
  }, [formData, isSubmitted, config, getFilledFieldsCount, getSessionId, getDeviceType]);

  // Set up exit event listeners
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        logAbandonment();
      }
    };

    const handleBeforeUnload = () => {
      logAbandonment();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [logAbandonment]);

  // Reset when form is submitted successfully
  useEffect(() => {
    if (isSubmitted) {
      hasLoggedRef.current = true; // Prevent logging after submission
    }
  }, [isSubmitted]);

  return { trackFieldChange };
}
